import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';

const AdminUserCreate = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    role: 'user',
    is_active: true,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Username validation
    if (!formData.username) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3 || formData.username.length > 30) {
      newErrors.username = 'Username must be 3-30 characters long';
    } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, hyphens, and underscores';
    }

    // Password validation (required)
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      // Prepare payload - password is now required
      const payload = {
        email: formData.email,
        username: formData.username,
        password: formData.password,
        role: formData.role,
        is_active: formData.is_active,
      };

      const response = await apiService.createAdminUser(payload);
      console.log('User created successfully:', response);
      
      // Navigate to the newly created user's detail page
      navigate(`/admin/users/${response.id}`);
    } catch (err) {
      console.error('Error creating user:', err);
      setSubmitError(err.message || 'Failed to create user. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.container}>
      <link href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />
      
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.titleSection}>
            <button style={styles.backButton} onClick={() => navigate('/admin/users')}>
              <i className="fas fa-arrow-left"></i>
            </button>
            <div>
              <h1 style={styles.title}>Create New User</h1>
              <p style={styles.subtitle}>Add a new user to the system</p>
            </div>
          </div>
        </div>
      </div>

      <div style={styles.formCard}>
        <form onSubmit={handleSubmit}>
          {submitError && (
            <div style={styles.errorAlert}>
              <i className="fas fa-exclamation-circle" style={styles.alertIcon}></i>
              {submitError}
            </div>
          )}

          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="email">
              Email <span style={styles.required}>*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              style={{...styles.input, ...(errors.email ? styles.inputError : {})}}
              placeholder="user@example.com"
              disabled={submitting}
            />
            {errors.email && <span style={styles.errorText}>{errors.email}</span>}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="username">
              Username <span style={styles.required}>*</span>
            </label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              style={{...styles.input, ...(errors.username ? styles.inputError : {})}}
              placeholder="johndoe"
              disabled={submitting}
            />
            {errors.username && <span style={styles.errorText}>{errors.username}</span>}
            <span style={styles.helpText}>
              3-30 characters, letters, numbers, hyphens, and underscores only
            </span>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="password">
              Password <span style={styles.required}>*</span>
            </label>
            <div style={styles.passwordWrapper}>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                style={{...styles.input, ...(errors.password ? styles.inputError : {}), ...styles.inputWithToggle}}
                placeholder="Enter password (min 8 characters)"
                disabled={submitting}
              />
              <button
                type="button"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                onClick={() => setShowPassword(s => !s)}
                style={styles.eyeButton}
              >
                <i className={showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
              </button>
            </div>
            {errors.password && <span style={styles.errorText}>{errors.password}</span>}
            <span style={styles.helpText}>
              Minimum 8 characters required
            </span>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="role">
                Role <span style={styles.required}>*</span>
              </label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                style={styles.select}
                disabled={submitting}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Status</label>
              <div style={styles.checkboxContainer}>
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  style={styles.checkbox}
                  disabled={submitting}
                />
                <label htmlFor="is_active" style={styles.checkboxLabel}>
                  Active User
                </label>
              </div>
              <span style={styles.helpText}>
                Inactive users cannot log in
              </span>
            </div>
          </div>

          <div style={styles.formActions}>
            <button
              type="button"
              style={styles.cancelButton}
              onClick={() => navigate('/admin/users')}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{...styles.submitButton, ...(submitting ? styles.submitButtonDisabled : {})}}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <div style={styles.spinner}></div>
                  Creating...
                </>
              ) : (
                <>
                  <i className="fas fa-user-plus" style={styles.buttonIcon}></i>
                  Create User
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
    padding: '20px',
    fontFamily: '"Open Sans", sans-serif',
  },
  header: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    padding: '24px 30px',
    marginBottom: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    border: '1px solid #e0e0e0',
  },
  headerContent: {
    maxWidth: '800px',
    margin: '0 auto',
  },
  titleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  backButton: {
    backgroundColor: 'transparent',
    border: '1px solid #2c5aa0',
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '16px',
    color: '#2c5aa0',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    margin: '0 0 4px 0',
    color: '#2c5aa0',
  },
  subtitle: {
    fontSize: '14px',
    margin: '0',
    color: '#666666',
  },
  formCard: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    padding: '32px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    border: '1px solid #e0e0e0',
    maxWidth: '800px',
    margin: '0 auto',
  },
  errorAlert: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
    border: '1px solid #f5c6cb',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
  },
  alertIcon: {
    fontSize: '16px',
  },
  formGroup: {
    marginBottom: '24px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#333333',
    marginBottom: '8px',
  },
  required: {
    color: '#dc3545',
  },
  optional: {
    color: '#666666',
    fontWeight: '400',
    fontSize: '12px',
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '16px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontFamily: '"Open Sans", sans-serif',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  },
  passwordWrapper: {
    position: 'relative',
    width: '100%',
  },
  inputWithToggle: {
    width: '100%',
    paddingRight: '40px',
    boxSizing: 'border-box',
  },
  eyeButton: {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#6b7280',
    fontSize: '16px',
    padding: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  inputError: {
    borderColor: '#dc3545',
  },
  select: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '16px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontFamily: '"Open Sans", sans-serif',
    outline: 'none',
    cursor: 'pointer',
    backgroundColor: 'white',
    boxSizing: 'border-box',
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '8px',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
  },
  checkboxLabel: {
    fontSize: '16px',
    color: '#333333',
    cursor: 'pointer',
    userSelect: 'none',
  },
  errorText: {
    display: 'block',
    color: '#dc3545',
    fontSize: '12px',
    marginTop: '4px',
  },
  helpText: {
    display: 'block',
    color: '#666666',
    fontSize: '12px',
    marginTop: '4px',
  },
  formActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '32px',
    paddingTop: '24px',
    borderTop: '1px solid #e0e0e0',
  },
  cancelButton: {
    backgroundColor: 'transparent',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#666666',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  submitButton: {
    backgroundColor: '#28a745',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    color: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
  },
  submitButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  buttonIcon: {
    fontSize: '14px',
  },
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid #ffffff',
    borderTop: '2px solid transparent',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
};

export default AdminUserCreate;
